import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const siteUrl = 'https://mycarapp.co.ke';
const siteName = 'My Car Kenya';

const pageMetadata = {
  '/': {
    title: 'Buy Quality Cars in Kenya | My Car Kenya',
    description: 'Browse quality cars for sale in Kenya, compare vehicles, arrange a test drive, and get trusted automotive services from My Car Kenya.',
    keywords: 'cars for sale in Kenya, buy a car in Kenya, used cars Kenya, car dealership Nairobi, My Car Kenya, automotive services Kenya, car hire Kenya, car parts Kenya, car accessories Kenya',
  },
  '/inventory': {
    title: 'Cars for Sale in Kenya | My Car Kenya Inventory',
    description: 'Explore cars for sale in Kenya. Search our vehicle inventory by make, model, body type, price, fuel type, and more.',
    keywords: 'cars for sale Kenya, used cars for sale Nairobi, vehicle inventory Kenya, affordable cars Kenya, SUVs Kenya',
  },
  '/about': {
    title: 'About My Car Kenya | Trusted Automotive Experts',
    description: 'Learn about My Car Kenya and our commitment to transparent car buying, quality vehicles, and dependable automotive services.',
    keywords: 'about My Car Kenya, trusted car dealer Kenya, automotive experts Nairobi, car dealership Kenya',
  },
  '/contact': {
    title: 'Contact My Car Kenya | Book a Test Drive',
    description: 'Contact My Car Kenya to book a test drive, ask about a vehicle, or get help finding the right car for your budget and lifestyle.',
    keywords: 'contact car dealer Kenya, book a test drive Nairobi, My Car Kenya phone, car showroom Kenya',
  },
  '/financing': {
    title: 'Car Financing in Kenya | My Car Kenya',
    description: 'Explore car financing options in Kenya and get guidance from the My Car Kenya team on making your next vehicle purchase affordable.',
    keywords: 'car financing Kenya, vehicle loans Kenya, car loan Nairobi, finance a car in Kenya',
  },
  '/parts': {
    title: 'Car Spare Parts and Accessories Kenya | My Car',
    description: 'Find genuine car spare parts and quality vehicle accessories in Kenya from My Car. Browse parts and accessories for your vehicle.',
    keywords: 'car spare parts Kenya, auto accessories Kenya, genuine car parts Nairobi, vehicle accessories Kenya',
  },
  '/accessories': {
    title: 'Car Accessories in Kenya | My Car',
    description: 'Shop useful and stylish car accessories in Kenya for comfort, safety, protection, and a better driving experience.',
    keywords: 'car accessories Kenya, auto accessories Nairobi, vehicle accessories for sale Kenya',
  },
  '/car-hire': {
    title: 'Car Hire in Kenya | My Car',
    description: 'Hire a reliable car in Kenya for business, travel, events, and everyday transport with My Car.',
    keywords: 'car hire Kenya, car rental Nairobi, vehicle hire Kenya, rent a car Nairobi, how much does it cost to hire a car in Kenya, car hire services Kenya, car hire companies Nairobi, cheap car hire Kenya, My Car Kenya car hire',
  },
  '/breakdown': {
    title: 'Car Breakdown Assistance Kenya | My Car',
    description: 'Get help when your vehicle breaks down. My Car provides dependable breakdown assistance and automotive support in Kenya.',
    keywords: 'car breakdown assistance Kenya, roadside assistance Nairobi, vehicle recovery Kenya, emergency car help, emergency car repair Kenya, My Car Kenya breakdown support, car repair services Nairobi',
  },
  '/buying': {
    title: 'How to Buy a Car in Kenya | My Car Kenya',
    description: 'Learn how to find, compare and buy a car in Kenya with My Car Kenya’s practical vehicle buying process.',
    keywords: 'how to buy a car in Kenya, car buying guide Kenya, buy used car Nairobi',
  },
  '/selling': {
    title: 'Sell Your Car in Kenya | My Car Kenya',
    description: 'Sell your car in Kenya through My Car Kenya and reach buyers looking for quality vehicles.',
    keywords: 'sell a car Kenya, sell car Nairobi, advertise vehicle Kenya',
  },
  '/terms': {
    title: 'Terms and Conditions | My Car Kenya',
    description: 'Read the terms and conditions governing use of the My Car Kenya marketplace.',
    keywords: 'My Car Kenya terms and conditions',
  },
  '/privacy': {
    title: 'Privacy Policy | My Car Kenya',
    description: 'Read how My Car Kenya collects, uses and protects marketplace account and contact-request information.',
    keywords: 'My Car Kenya privacy policy',
  },
  '/garage-index': {
    title: 'Garage Management Software in Kenya | My Car',
    description: 'Manage garage bookings, customers, work orders, invoices and daily operations with My Car garage management software.',
    keywords: 'garage management software Kenya, auto garage software Nairobi, workshop management system Kenya',
  },
};

const upsertMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

export function updatePageMetadata({ title, description, keywords, canonicalUrl, robots = 'index, follow', type = 'website', image = `${siteUrl}/icon.png`, structuredData }) {
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'keywords', keywords);
  upsertMeta('name', 'robots', robots);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonicalUrl);
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:site_name', siteName);
  upsertMeta('property', 'og:image', image);
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);
  upsertLink('canonical', canonicalUrl);

  let script = document.head.querySelector('script[data-seo-jsonld]');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.seoJsonld = 'true';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(structuredData);
}

export default function SEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isPrivatePage = ['/admin-login', '/admin', '/add-listing', '/garage', '/garage-login', '/signup', '/login', '/seller-login'].includes(pathname);
    const isKnownPublicPage = Boolean(pageMetadata[pathname])
      || ['/inventory/', '/parts/', '/accessories/'].some((prefix) => pathname.startsWith(prefix));
    const metadata = pageMetadata[pathname] || {
      title: 'My Car Kenya | Cars, Parts and Automotive Services',
      description: 'My Car Kenya helps you find quality vehicles, car parts, accessories, financing, hire, and automotive support in Kenya.',
      keywords: 'My Car Kenya, cars Kenya, automotive services Kenya, car parts Kenya',
    };
    const canonicalPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
    const canonicalUrl = `${siteUrl}${canonicalPath}`;
    const robots = isPrivatePage || !isKnownPublicPage ? 'noindex, nofollow' : 'index, follow';
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': pathname === '/' ? 'AutoDealer' : 'WebPage',
          name: metadata.title,
          description: metadata.description,
          url: canonicalUrl,
          isPartOf: { '@id': `${siteUrl}/#website` },
        },
        {
          '@type': 'AutoDealer',
          '@id': `${siteUrl}/#business`,
          name: siteName,
          url: siteUrl,
          image: `${siteUrl}/icon.png`,
          address: { '@type': 'PostalAddress', addressCountry: 'KE', addressLocality: 'Nairobi' },
          areaServed: { '@type': 'Country', name: 'Kenya' },
        },
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          name: siteName,
          url: siteUrl,
          publisher: { '@id': `${siteUrl}/#business` },
        },
      ],
    };
    const breadcrumbNames = pathname.split('/').filter(Boolean);
    if (breadcrumbNames.length) {
      structuredData['@graph'].push({
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          ...breadcrumbNames.map((segment, index) => ({
            '@type': 'ListItem',
            position: index + 2,
            name: segment.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
            item: `${siteUrl}/${breadcrumbNames.slice(0, index + 1).join('/')}`,
          })),
        ],
      });
    }
    updatePageMetadata({
      title: metadata.title,
      description: metadata.description,
      keywords: metadata.keywords,
      canonicalUrl,
      robots,
      type: pathname === '/' ? 'website' : 'article',
      structuredData,
    });
  }, [pathname]);

  return null;
}